<script setup lang="ts">
import {computed, ref, onMounted, Ref} from 'vue';
import { useRootStore } from '@/store';
import { Modal, SignatureData } from '@/store/classes';
import { SignatureDocumentFieldType } from '@/store/enums';
import { BLANK_IMG } from '@/components/common/schemas';
import { usePortalIntakeStore } from '@/store/PortalIntakeModule';
import { ERROR_ANCHOR_CLASS } from '@/components/common/FormValidation';
import FormField from "@/components/common/FormField.vue3.vue"

const props = withDefaults(defineProps<{
    height?: number,
    mode?: 'signature' | 'initials',
    value?: string,
    documentIndex: number,
    fieldIndex: number
}>(), {
    height: 90,
    mode: 'signature',
});

const emit = defineEmits<{ (e: 'update'): void }>()

const intakeStore = usePortalIntakeStore();
const patientSignature = computed(() => intakeStore.state.patientSignature);
const patientInitials = computed(() => intakeStore.state.patientInitials)
const setPatientSignature = (signature: SignatureData) => intakeStore.dispatch('setPatientSignature', signature);
const setPatientInitials = (initials: SignatureData) => intakeStore.dispatch('setPatientInitials', initials);
const setDocumentField = (docIndex: number, fieldIndex: number, field: {type: SignatureDocumentFieldType, value: Ref<string|undefined>}) =>
    intakeStore.dispatch('setDocumentField', {docIndex, fieldIndex, field});

const width = computed(() => props.height * (props.mode === 'signature' ? 5.5 : 2.5));
const displayHeight = computed(() => props.height * .75)
const displayWidth = computed(() => width.value * .75);

const rootStore = useRootStore();
const showModal = (modal: Modal) => rootStore.dispatch('showModal', modal);
const currentModal = computed(() => rootStore.state.currentModal);
const imgSrc = ref(BLANK_IMG);
const isEmpty = (src = imgSrc.value) => !src || src === BLANK_IMG;

const validationState = computed(() => intakeStore.getters.getSignatureFieldValidationState({ docIndex: props.documentIndex, fieldIndex: props.fieldIndex }));

onMounted(() =>
    setDocumentField(props.documentIndex, props.fieldIndex, {type: SignatureDocumentFieldType.Signature, value: imgSrc})
);

async function openSignatureModal() {
    if (currentModal.value?.name === 'SurveyPreviewModal') 
        return; // don't open if we're in the preview modal

    const storedSignature = props.mode === 'initials' ? patientInitials.value : patientSignature.value;
    const newSig: SignatureData = await showModal({
        name: 'SignatureModal',
        props: {
            storedSignature,
            isInitials: props.mode === 'initials',

            width: width.value,
            height: props.height,
        }
    });

    imgSrc.value = newSig.imgSrc;

    if (!isEmpty(newSig.imgSrc) || !!newSig.text) {
        if (props.mode === 'initials')
            setPatientInitials(newSig);
        else
            setPatientSignature(newSig);
    }

    emit('update');
}

</script>
<template>
    <form-field :validation-state="validationState">
        <span
            class="signature-container signature-form-signature"
            :class="[
                props.mode === 'initials' ? 'signature-initials' : 'signature-full',
                (validationState?.invalid && validationState?.submitAttempted) && (ERROR_ANCHOR_CLASS + ' field-validation-error'),
                isEmpty() && 'placeholder-visible'

            ]"
            :style="{ maxWidth: displayWidth + 'px', maxHeight: displayHeight + 'px'}"
            @click="() => openSignatureModal()"
        >
            <img
                :src="imgSrc"
                class="signature-image"
                :width="displayWidth"
                :height="displayHeight"
                style="margin-top: -1px; margin-left: -1px"
            >
        </span>
    </form-field>
</template>
<style scoped lang="scss">
    .signature-container {
        border-radius: 4px;
        display: block;
        margin: 2px;
        border: 1px solid #aaa;
        padding: 1px;
        cursor: pointer;

        &:hover {
            outline: 2px #0057b8 solid;
        }
        
        &.placeholder-visible {
            &::before {
                border-bottom: 1px solid rgba(0, 0, 0, 0.6);
                color: rgba(0, 0, 0, 0.6);
                display: block;
                font-weight: normal;
                margin: 0 15px;
                padding-left: 5px;
                position: relative;
                top: 30px;
            }
            &.signature-initials::before {
                content: 'Initial here';
            }
            &.signature-full::before {
                content: 'Sign here';
            }
        }
    }
</style>

